/**
 * Enhanced API client with timeouts, retries, cache and telemetry
 * Resilient networking for production
 */

import { readCache } from './cache.js';
import { prewarmForCriticalOperation } from './warmup.js';

// URL helper function for API endpoints (unchanged)
const RAW = import.meta.env.VITE_API_URL || "";
const BASE = RAW.replace(/\/+$/, "");
const HAS_API_SUFFIX = /\/api$/i.test(BASE);
const API_PREFIX = HAS_API_SUFFIX ? "" : "/api";

export const apiUrl = (path = "") => {
  let p = String(path || "");
  p = p.replace(/^https?:\/\/[^/]+/i, "");
  p = p.replace(/^\/api\b/i, "");
  if (!p.startsWith("/")) p = `/${p}`;

  const url = `${BASE}${API_PREFIX}${p}`.replace(/([^:]\/)\/+/g, "$1");

  if (import.meta.env.VITE_DEBUG_API === 'true') {
    window.__DBG__ ??= { calls: [] };
    if (window.__DBG__.calls.length < 5) {
      window.__DBG__.calls.push({ RAW, BASE, HAS_API_SUFFIX, API_PREFIX, pathOriginal: path, pathNormalizado: p, url });
    }
  }
  return url;
};

// Telemetry for development
const isDev = import.meta.env.DEV;
if (isDev && typeof window !== 'undefined') {
  window.__telemetry = window.__telemetry || { netErrors: [] };
}

const logNetError = (url, attempt, reason) => {
  if (isDev && typeof window !== 'undefined') {
    window.__telemetry.netErrors.push({
      url,
      attempt,
      reason,
      timestamp: Date.now()
    });
    
    // Keep only last 50 errors
    if (window.__telemetry.netErrors.length > 50) {
      window.__telemetry.netErrors = window.__telemetry.netErrors.slice(-50);
    }
  }
};

// Request deduplication for GET requests
const pendingGets = new Map();

// API call rate limiting - prevent spam
const API_RATE_LIMITS = {
  '/missions': { window: 5000, maxCalls: 3 }, // 3 calls per 5 seconds
  '/missions/active': { window: 3000, maxCalls: 2 }, // 2 calls per 3 seconds
  '/travel/status': { window: 3000, maxCalls: 2 }, // 2 calls per 3 seconds
  '/travel/cities': { window: 10000, maxCalls: 2 }, // 2 calls per 10 seconds
  '/player/profile': { window: 2000, maxCalls: 1 } // 1 call per 2 seconds
};

const apiCallHistory = new Map();

// Check if API call is allowed (rate limiting)
const isApiCallAllowed = (endpoint) => {
  // Find matching rate limit pattern
  const rateLimitKey = Object.keys(API_RATE_LIMITS).find(pattern => 
    endpoint.includes(pattern)
  );
  
  if (!rateLimitKey) return true; // No rate limit for this endpoint
  
  const { window, maxCalls } = API_RATE_LIMITS[rateLimitKey];
  const now = Date.now();
  const calls = apiCallHistory.get(rateLimitKey) || [];
  
  // Remove calls outside the window
  const recentCalls = calls.filter(timestamp => now - timestamp < window);
  
  if (recentCalls.length >= maxCalls) {
    console.warn(`Rate limit exceeded for ${rateLimitKey}. ${recentCalls.length}/${maxCalls} calls in last ${window}ms`);
    return false;
  }
  
  // Add current call to history
  recentCalls.push(now);
  apiCallHistory.set(rateLimitKey, recentCalls);
  
  return true;
};

// Sleep utility
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Cold start tolerance for first public calls
let firstPublicAttempt = true;

// Helper for public endpoints with cold start tolerance
const getWithWarmup = async (api, url, opts = {}) => {
  try {
    return await api.get(url, opts);
  } catch (err) {
    const isPublic = url.includes('/health') || url.includes('/public/config');
    if (isPublic && firstPublicAttempt) {
      firstPublicAttempt = false;
      console.log('🔥 Cold start detected, retrying with extended timeout...');
      // Retry once with +3000ms timeout
      const extendedTimeout = (opts?.timeoutMs || 12000) + 3000;
      return await api.request(url, { 
        ...opts, 
        timeout: extendedTimeout 
      });
    }
    throw err;
  }
};

// Toast utility
const showToast = (type, text) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('toast', { 
      detail: { type, text }
    }));
  }
};

// Retry delays with exponential backoff for 429 errors: [0ms, 2s, 5s, 10s]
const getRetryDelay = (attempt, is429Error = false) => {
  if (attempt === 0) return 0;
  
  if (is429Error) {
    // Longer delays for rate limit errors
    if (attempt === 1) return 2000;  // 2 seconds
    if (attempt === 2) return 5000;  // 5 seconds
    if (attempt === 3) return 10000; // 10 seconds
    return 15000; // 15 seconds for subsequent attempts
  }
  
  // Standard delays for other errors
  if (attempt === 1) return 400;
  if (attempt === 2) return 1200;
  return 0;
};

class EnhancedApiClient {
  constructor() {
    this.baseURL = `${BASE}${API_PREFIX}`;
    this.healthURL = BASE;
    this.token = localStorage.getItem('auth:token');
    this.defaultTimeout = 12000; // 12 seconds
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth:token', token);
    } else {
      localStorage.removeItem('auth:token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('auth:token');
  }

  clearAuth() {
    this.token = null;
    localStorage.removeItem('auth:token');
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Enhanced request with timeout, retries, cache and deduplication
   */
  async request(endpoint, options = {}) {
    const url = apiUrl(endpoint);
    const method = options.method || 'GET';
    const isGetRequest = method === 'GET';
    const timeout = options.timeout || this.defaultTimeout;
    
    // Frontend rate limiting check (less aggressive to allow backend to handle it)
    if (!isApiCallAllowed(endpoint)) {
      console.warn(`Frontend rate limit exceeded for ${endpoint}`);
      // Don't immediately reject, let it go to backend and handle 429 properly
    }
    
    // Deduplication for GET requests
    if (isGetRequest && pendingGets.has(url)) {
      return pendingGets.get(url);
    }

    const requestPromise = this._executeRequest(url, method, options, timeout);
    
    // Store promise for deduplication
    if (isGetRequest) {
      pendingGets.set(url, requestPromise);
      
      // Clean up after completion
      requestPromise.finally(() => {
        pendingGets.delete(url);
      });
    }

    return requestPromise;
  }

  async _executeRequest(url, method, options, timeout) {
    const isGetRequest = method === 'GET';
    let maxAttempts = isGetRequest ? 3 : 1; // Base retry attempts
    
    let lastError = null;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Add delay before retry (except first attempt)
      if (attempt > 0) {
        const is429Error = lastError && lastError.status === 429;
        const delay = getRetryDelay(attempt, is429Error);
        
        if (is429Error) {
          console.warn(`Rate limited, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxAttempts})`);
        }
        
        await sleep(delay);
      }

      try {
        return await this._singleRequest(url, method, options, timeout, attempt);
      } catch (error) {
        lastError = error;
        
        // Special handling for 429 rate limit errors
        if (error.status === 429) {
          console.warn(`Rate limit hit for ${url}, attempt ${attempt + 1}`);
          // Don't retry 429 errors aggressively - respect the rate limit
          break;
        }
        
        // Don't retry 401 errors - they indicate auth issues
        if (error.status === 401) {
          console.warn(`Authentication required for ${url}`);
          break;
        }
        
        // Log network errors for telemetry
        if (isGetRequest && (error.name === 'AbortError' || error.name === 'NetworkError')) {
          logNetError(url, attempt + 1, error.message);
        }

        // Don't retry on auth errors or other client errors (except 429)
        if (error.status >= 400 && error.status < 500 && error.status !== 429) {
          break;
        }
        
        // Don't retry on last attempt
        if (attempt === maxAttempts - 1) {
          break;
        }
      }
    }

    // If all retries failed and it's a GET request, try cache
    if (isGetRequest && lastError) {
      const cachedData = await this._tryCache(url);
      if (cachedData) {
        showToast('warning', 'No se pudo conectar. Mostrando datos guardados.');
        return cachedData;
      }
    }

    throw lastError;
  }

  async _singleRequest(url, method, options, timeout, attempt) {
    const config = {
      method,
      headers: this.getHeaders(),
      ...options,
    };

    // Add body for non-GET requests
    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    config.signal = controller.signal;

    // Set timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      // Handle response
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          this.clearAuth();
          showToast('warning', 'Sesión expirada');
          window.dispatchEvent(new CustomEvent('app:logout'));
          throw new ApiError('Authentication required', response.status, data);
        }
        
        throw new ApiError(
          data?.message || `HTTP ${response.status}`,
          response.status,
          data
        );
      }

      // Cache successful GET responses
      if (method === 'GET' && response.ok) {
        await this._cacheResponse(url, data);
      }

      return data;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new ApiError('Request timeout', 0, { timeout: true });
      }
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network or other errors
      throw new ApiError(
        'Network error or server unavailable',
        0,
        { originalError: error.message, networkError: true }
      );
    }
  }

  async _tryCache(url) {
    try {
      // Check if offline
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const cacheKey = readCache.constructor.getApiKey(url);
        return await readCache.get(cacheKey);
      }
      
      // For timeout errors, also try cache
      const cacheKey = readCache.constructor.getApiKey(url);
      return await readCache.get(cacheKey);
    } catch (error) {
      return null;
    }
  }

  async _cacheResponse(url, data) {
    try {
      // Don't cache error responses or auth responses
      if (data && typeof data === 'object' && !data.error) {
        const cacheKey = readCache.constructor.getApiKey(url);
        await readCache.set(cacheKey, data);
      }
    } catch (error) {
      // Ignore cache errors
    }
  }

  // HTTP methods with enhanced behavior
  async get(endpoint, params = {}) {
    const baseUrl = apiUrl(endpoint);
    const url = new URL(baseUrl);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });
    
    return this.request(url.pathname + url.search);
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data,
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data,
    });
  }

  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data,
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // Authentication methods
  async login(email, password) {
    const LOGIN_TIMEOUT = 20000; // 20 seconds for cold start tolerance
    const MAX_RETRIES = 2; // Original attempt + 1 retry
    
    console.log('🔥 Pre-warming backend before login...');
    // Pre-warm backend to reduce login latency
    await prewarmForCriticalOperation();
    
    let lastError;
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`🔐 Login attempt ${attempt}/${MAX_RETRIES}${attempt > 1 ? ' (retrying after cold start)' : ''}`);
        
        const response = await this.request('/auth/login', {
          method: 'POST',
          body: { email, password },
          timeout: LOGIN_TIMEOUT
        });
        
        if (response.token) {
          this.setToken(response.token);
          
          // Preload critical endpoints after successful login (background)
          this.preloadCriticalEndpoints();
          
          // Backend warmup to prevent cold starts (non-blocking)
          setTimeout(() => {
            fetch(`${this.healthURL}/health`).catch(() => {});
          }, 0);
          setTimeout(() => {
            this.get('/public/config').catch(() => {});
          }, 200);
        }
        
        console.log('✅ Login successful');
        return response;
        
      } catch (error) {
        lastError = error;
        const isNetworkError = error.message.includes('fetch') || 
                               error.message.includes('Network') ||
                               error.message.includes('timeout') ||
                               error.status === 0 ||
                               error.status >= 500;
        
        if (isNetworkError && attempt < MAX_RETRIES) {
          console.warn(`⚠️ Login attempt ${attempt} failed (${error.message}), retrying...`);
          // Brief delay before retry
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        
        // Non-retryable error or max retries reached
        throw error;
      }
    }
    
    throw lastError;
  }
  
  // Background preloading of critical endpoints after login
  async preloadCriticalEndpoints() {
    const calls = [
      this.get('/player/profile'),
      this.get('/missions/active'),
      this.get('/travel/cities'),
      this.get('/travel/status')
    ];

    for (const fn of calls) {
      try {
        await Promise.race([
          fn,
          new Promise((_, rej) => setTimeout(() => rej(new Error('preload-timeout')), 8000)),
        ]);
        console.log('📡 Preload success');
      } catch (e) {
        console.warn('[preload] soft-fail:', e?.message || e);
      }
      await new Promise(r => setTimeout(r, 150));
    }
  }

  async register(email, password, username) {
    const response = await this.post('/auth/register', { email, password, username });
    if (response.token) {
      this.setToken(response.token);
    }
    return response;
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  async getProfile() {
    return this.get('/auth/me');
  }

  // Player methods
  async getPlayerProfile() {
    return this.get('/player/profile');
  }

  async updatePlayerStats(stats) {
    return this.put('/player/stats', stats);
  }

  async updatePlayerCoins(amount, operation = 'set') {
    return this.put('/player/coins', { amount, operation });
  }

  async useItem(itemId, quantity = 1) {
    return this.post('/player/use-item', { itemId, quantity });
  }

  // Mission methods
  async getMissions() {
    return this.get('/missions');
  }

  async startMission(missionId) {
    return this.post(`/missions/${missionId}/start`);
  }

  async getActiveMission() {
    return this.get('/missions/active');
  }

  async completeMission() {
    return this.post('/missions/complete');
  }

  async getMissionHistory(page = 1, limit = 10) {
    return this.get('/missions/history', { page, limit });
  }

  // Travel methods
  async getCities() {
    return this.get('/travel/cities');
  }

  async getTravelPreview(destinationId) {
    return this.get(`/travel/preview/${destinationId}`);
  }

  async startTravel(destinationId) {
    return this.post('/travel/start', { destinationId });
  }

  async getTravelStatus() {
    return this.get('/travel/status');
  }

  async completeTravel() {
    return this.post('/travel/complete');
  }

  async getTravelHistory(page = 1, limit = 10) {
    return this.get('/travel/history', { page, limit });
  }

  // Inventory methods
  async getInventory() {
    return this.get('/inventory');
  }

  async getItems(filters = {}) {
    return this.get('/inventory/items', filters);
  }

  async useInventoryItem(itemId, quantity = 1) {
    return this.post('/inventory/use', { itemId, quantity });
  }

  async addInventoryItem(itemId, quantity = 1) {
    return this.post('/inventory/add', { itemId, quantity });
  }

  async removeInventoryItem(playerItemId, quantity = null) {
    const data = quantity ? { quantity } : {};
    return this.delete(`/inventory/${playerItemId}`, data);
  }

  async getInventorySummary() {
    return this.get('/inventory/summary');
  }

  // Health check (uses base URL without API prefix)
  async healthCheck() {
    const url = `${this.healthURL}/health`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new ApiError(`Health check failed: ${response.status}`, response.status);
      }
      
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  // Get public configuration (no auth required)
  async getPublicConfig() {
    return this.get('/public/config');
  }
  
  // Admin API helpers (unchanged structure)
  get admin() {
    return {
      health: () => this.get('/admin/health'),
      metrics: () => this.get('/admin/metrics/summary'),
      metricsSeries: (params) => this.get('/admin/metrics/series', params),
      maintenanceGet: () => this.get('/admin/config/maintenance'),
      maintenanceSet: (body) => this.patch('/admin/config/maintenance', body),
      
      flagsList: (q = '') => this.get('/admin/flags' + q),
      flagUpsert: (key, value) => this.put(`/admin/flags/${encodeURIComponent(key)}`, { value }),
      
      cities: {
        list: () => this.get('/admin/cities'),
        create: (b) => this.post('/admin/cities', b),
        update: (id, b) => this.put(`/admin/cities/${id}`, b),
        remove: (id) => this.delete(`/admin/cities/${id}`)
      },
      
      items: {
        list: () => this.get('/admin/items'),
        create: (b) => this.post('/admin/items', b),
        update: (id, b) => this.put(`/admin/items/${id}`, b),
        remove: (id) => this.delete(`/admin/items/${id}`)
      },
      
      missions: {
        list: () => this.get('/admin/missions'),
        create: (b) => this.post('/admin/missions', b),
        update: (id, b) => this.put(`/admin/missions/${id}`, b),
        remove: (id) => this.delete(`/admin/missions/${id}`)
      },
      
      players: {
        list: (q = '') => this.get('/admin/players' + q),
        get: (id) => this.get(`/admin/players/${id}`),
        grant: (id, b) => this.post(`/admin/players/${id}/grant`, b)
      },
      
      economy: {
        factors: () => this.get('/admin/economy/factors'),
        override: (b) => this.post('/admin/economy/override', b),
        logs: (q = '') => this.get('/admin/economy/logs' + q)
      },
      
      exportContent: (type) => this.get(`/admin/export/content/${type}`),
      importContent: (body) => this.post('/admin/import/content', body),
      
      auditLogs: (q = '') => this.get('/admin/audit/logs' + q),
      
      crafting: {
        list: () => this.get('/admin/crafting/recipes'),
        create: (b) => this.post('/admin/crafting/recipes', b),
        remove: (id) => this.delete(`/admin/crafting/recipes/${id}`)
      },

      shop: {
        stock: (q = '') => this.get('/admin/shop/stock' + q),
        updateStock: (id, b) => this.patch(`/admin/shop/stock/${id}`, b),
        restock: (b) => this.post('/admin/shop/restock', b),
        sales: (q = '') => this.get('/admin/shop/sales' + q)
      }
    };
  }

  // Crafting API helpers
  async getRecipes() {
    return this.get('/crafting/recipes');
  }

  async postCraft({ recipeId, times = 1 }) {
    return this.post('/crafting/craft', { recipeId, times });
  }

  // Shop API helpers
  async getShopStock() {
    return this.get('/shop/stock');
  }

  async buyItem({ itemId, quantity = 1 }) {
    return this.post('/shop/buy', { itemId, quantity });
  }

  async sellItem({ itemId, quantity = 1 }) {
    return this.post('/shop/sell', { itemId, quantity });
  }

  // Economy API helpers
  async getEconomyToday() {
    return this.get('/economy/today');
  }

  async getEconomyStats(params = {}) {
    return this.get('/economy/stats', params);
  }
}

// Custom error class (unchanged)
class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
  
  isAuthError() {
    return this.status === 401;
  }
  
  isNotFoundError() {
    return this.status === 404;
  }
  
  isValidationError() {
    return this.status === 400;
  }
  
  isServerError() {
    return this.status >= 500;
  }

  isTimeoutError() {
    return this.data?.timeout === true;
  }

  isNetworkError() {
    return this.data?.networkError === true;
  }
}

// Create singleton instance
const api = new EnhancedApiClient();

// Export both the instance and the class
export default api;
export { api, EnhancedApiClient as ApiClient, ApiError, getWithWarmup };

// Legacy http function for compatibility
export async function http(path, options = {}) {
  const res = await fetch(apiUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(localStorage.getItem('auth:token') ? { Authorization: `Bearer ${localStorage.getItem('auth:token')}` } : {})
    }
  });
  
  if (res.status === 401) {
    localStorage.removeItem('auth:token');
    showToast('warning', 'Sesión expirada');
    window.dispatchEvent(new CustomEvent('app:logout'));
  }
  
  return res;
}